import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Indicator } from 'is24-corecss'
import { IconS24CheckmarkCircle24Filled } from '@is24/cosma-ui-icons'
import { AttachmentData, AttachmentStatus } from '../../../../../../../../../../../@types/types'
import { convertImageUrl } from '../../../../../../../../../../../services/conversion/conversionServices'
import { PictureLoadingPlaceholder } from '../pictureLoadingPlaceholder/PictureLoadingPlaceholder'
import styles from './SortablePictureItem.module.css'
import { useTranslation } from 'react-i18next'

export interface SortablePictureItemProps {
  data: AttachmentData
  index: number
  isSelected?: boolean
  onClick?: () => void
}

export const SortablePictureItem: React.FC<SortablePictureItemProps> = ({
  data,
  index,
  isSelected = false,
  onClick,
}) => {
  const { listeners, isDragging, setNodeRef, transform, transition } = useSortable({
    id: data.attachment?.id || data.file?.id || '',
  })

  const dynamicStyles = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    visibility: isDragging ? ('hidden' as const) : undefined,
  }

  const isTitleImage = index === 0
  const isLoading = data.state === AttachmentStatus.UPLOADING
  const uploadProgress = data.file?.percent || 0

  const className = [
    styles.pictureItem,
    'display-flex align-items-center justify-content-center',
    onClick ? styles.clickable : '',
    isSelected ? styles.selected : '',
    isTitleImage ? styles.titleImage : '',
  ]
    .filter(Boolean)
    .join(' ')

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onClick?.()
  }

  // Render loading placeholder instead of image when uploading
  if (isLoading) {
    return (
      <div ref={setNodeRef} className={`${className} ${styles.loadingPlaceholder} padding-xs`} style={dynamicStyles}>
        <PictureLoadingPlaceholder percent={uploadProgress} />
      </div>
    )
  }

  return (
    <div ref={setNodeRef} className={className} style={dynamicStyles}>
      {/* Image background with drag listeners - always applied */}
      <div
        {...listeners}
        className={styles.imageBackground}
        style={{
          backgroundImage: `url(${convertImageUrl('320', '160', data.attachment?.url || '')})`,
        }}
        onClick={handleClick}
      />

      <TitleIndicator isTitleImage={isTitleImage} />
      <SelectedCheckmark isSelected={isSelected} />
    </div>
  )
}

const TitleIndicator = ({ isTitleImage }: { isTitleImage: boolean }): React.JSX.Element => {
  const { t } = useTranslation()

  return (
    <>
      {isTitleImage && (
        <div className={styles.labelContainer}>
          <Indicator appearance='label' labelColor='white' labelVariant='flat'>
            {t('wizard.screen.multimedia.webview.modal.picture.titlePicture')}
          </Indicator>
        </div>
      )}
    </>
  )
}

const SelectedCheckmark = ({ isSelected }: { isSelected: boolean }): React.JSX.Element => {
  return (
    <>
      {isSelected && (
        <div className={styles.checkmark}>
          <IconS24CheckmarkCircle24Filled />
        </div>
      )}
    </>
  )
}
